import { Link } from "react-router-dom";
import { ArrowRight, Zap, ShoppingBag, Tag, Sparkles, CheckCircle2, Camera, Layers } from "lucide-react";

export default function Landing() {
    return (
        <div data-testid="landing-page" className="relative">
            {/* Ambient glow */}
            <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
                <div className="absolute top-[-200px] left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-full bg-accent/5 blur-[120px]" />
                <div className="absolute bottom-[-100px] right-[-100px] w-[500px] h-[500px] rounded-full bg-accent/[0.03] blur-[100px]" />
            </div>

            {/* HERO */}
            <section className="relative">
                <div className="max-w-6xl mx-auto px-6 pt-24 pb-32 text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-surface mb-8 animate-fade-in">
                        <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                        <span className="overline !text-zinc-400">AI listing engine — live now</span>
                    </div>

                    <h1 className="font-heading text-5xl md:text-7xl lg:text-[5.5rem] font-bold leading-[1.02] tracking-tighter mb-6 animate-fade-in">
                        Turn product photos<br />
                        into <span className="text-accent">listings that sell.</span>
                    </h1>

                    <p className="text-lg md:text-xl text-zinc-400 max-w-2xl mx-auto mb-10 leading-relaxed animate-fade-in">
                        Upload an image, drop basic specs, and get SEO titles, persuasive descriptions, bullet points and meta tags — formatted for Amazon, Shopify, Etsy &amp; eBay. In under 30 seconds.
                    </p>

                    <div className="flex flex-wrap gap-3 justify-center animate-fade-in">
                        <Link to="/register" className="btn-primary" data-testid="hero-get-started">
                            Start free <ArrowRight className="w-4 h-4" />
                        </Link>
                        <Link to="/showcase" className="btn-ghost" data-testid="hero-see-examples">
                            See real examples
                        </Link>
                    </div>

                    <div className="flex items-center gap-6 mt-10 justify-center text-sm text-zinc-500">
                        <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-accent/70" /> 1 free listing</div>
                        <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-accent/70" /> No card required</div>
                        <div className="hidden sm:flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-accent/70" /> Cancel anytime</div>
                    </div>
                </div>

                {/* Marketplace strip */}
                <div className="max-w-6xl mx-auto px-6 pb-16">
                    <div className="text-center overline mb-8">Built for</div>
                    <div className="flex flex-wrap justify-center items-center gap-x-12 gap-y-4 text-zinc-600 text-xl font-mono uppercase tracking-widest">
                        <span>Amazon</span>
                        <span className="text-zinc-800">/</span>
                        <span>Shopify</span>
                        <span className="text-zinc-800">/</span>
                        <span>Etsy</span>
                        <span className="text-zinc-800">/</span>
                        <span>eBay</span>
                        <span className="text-zinc-800">/</span>
                        <span>Walmart</span>
                    </div>
                </div>
            </section>

            {/* Divider */}
            <div className="divider-line max-w-6xl mx-auto" />

            {/* FEATURES */}
            <section className="py-24">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="text-center mb-16">
                        <div className="overline mb-3">Capabilities</div>
                        <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight max-w-3xl mx-auto">
                            Everything you need to ship a listing today.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {[
                            { icon: Camera, title: "Vision-first", text: "Reads color, material and design from your photo so copy actually matches the product." },
                            { icon: Zap, title: "SEO that ranks", text: "Keyword-rich titles, meta tags and search-optimized descriptions tuned per marketplace." },
                            { icon: Tag, title: "Variant copy", text: "Auto-generated descriptions for every color, size or style — at no extra cost." },
                            { icon: Layers, title: "Marketplace-aware", text: "Amazon bullets, Etsy story-telling, eBay 80-char titles, Shopify HTML. One click each." },
                            { icon: Sparkles, title: "Persuasive, not generic", text: "Tuned prompts produce copy that converts. No AI mush, no filler." },
                            { icon: ShoppingBag, title: "Bulk-ready", text: "Premium queue gets priority during launch crunch. Scale to 100s of listings." },
                        ].map((f) => (
                            <div key={f.title} className="surface surface-hover p-6 group">
                                <div className="w-9 h-9 rounded-md border border-white/10 bg-surface-2 flex items-center justify-center mb-4 group-hover:border-accent/40 transition-colors">
                                    <f.icon className="w-4 h-4 text-zinc-400 group-hover:text-accent transition-colors" strokeWidth={1.75} />
                                </div>
                                <h3 className="font-semibold text-zinc-100 mb-1.5">{f.title}</h3>
                                <p className="text-sm text-zinc-500 leading-relaxed">{f.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <div className="divider-line max-w-6xl mx-auto" />

            {/* HOW IT WORKS */}
            <section className="py-24">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="text-center mb-16">
                        <div className="overline mb-3">Workflow</div>
                        <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">From photo to published in 3 steps.</h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                        {[
                            { n: "01", title: "Upload photos", text: "Drop up to 4 product images. JPG, PNG or WEBP." },
                            { n: "02", title: "Add basic specs", text: "Title, category, keywords. Skip the parts you don't know." },
                            { n: "03", title: "Get copy. Ship.", text: "SEO title, bullets, meta tags & variants — formatted per marketplace." },
                        ].map((s) => (
                            <div key={s.n} className="surface p-8">
                                <div className="font-mono text-accent text-sm mb-6">{s.n}</div>
                                <h3 className="font-heading text-2xl font-semibold tracking-tight mb-2">{s.title}</h3>
                                <p className="text-zinc-500 leading-relaxed">{s.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <div className="divider-line max-w-6xl mx-auto" />

            {/* CTA */}
            <section className="py-24">
                <div className="max-w-3xl mx-auto px-6 text-center">
                    <h2 className="font-heading text-4xl md:text-6xl font-bold tracking-tight mb-5">
                        Your next listing<br />writes itself.
                    </h2>
                    <p className="text-zinc-400 text-lg mb-10">Start free. Pay only when you scale.</p>
                    <Link to="/register" className="btn-primary glow-accent" data-testid="cta-final">
                        <Sparkles className="w-4 h-4" /> Try listcraft free
                    </Link>
                </div>
            </section>
        </div>
    );
}
